import type { CrossFrameworkLib } from '../../cross-framework-lib';
type GlobalObj = Window & typeof globalThis;

/**
 * Retry options for the import mechanism
 */
interface RetryOptions {
  /** Maximum number of retry attempts */
  maxRetries?: number;
  /** Initial delay between retries in ms */
  initialDelay?: number;
  /** Maximum delay between retries in ms */
  maxDelay?: number;
  /** Factor to multiply delay by after each attempt */
  backoffFactor?: number;
  /** Timeout in ms after which to abort all attempts */
  timeout?: number;
}

/** Function to import the CrossFrameworkLib module, to make code more testable */
const importLib = () => import('../../cross-framework-lib');

/**
 * Import the CrossFrameworkLib from the global object or from the cross-framework-lib module.
 */
export async function importCrossFrameworkLib(
  /** Dependency injection of window object */
  globalObj: GlobalObj = window,
  /** Dependency injection of dynamic import function */
  importDynamically = importLib,
  /** Retry options */
  retryOptions: RetryOptions = {}
): Promise<CrossFrameworkLib> {
  return new Promise((resolve, reject) => {
    // Default retry options
    const defaultRetryOptions = {
      maxRetries: 5,
      initialDelay: 1000, // 1 second
      maxDelay: 10000, // 10 seconds
      backoffFactor: 2,
      timeout: 60000, // 60 seconds
      ...retryOptions,
    };

    const isCrossFrameworkLibReady = () => 'CrossFrameworkLib' in globalObj;

    // If CrossFrameworkLib already exists in global, use it
    if (isCrossFrameworkLibReady()) {
      resolve(globalObj.CrossFrameworkLib);
      return;
    }

    // Set a timeout to avoid hanging forever
    const timeoutId = setTimeout(() => {
      reject(new Error('CrossFrameworkLib: Timeout waiting for CrossFrameworkLib to load'));
    }, defaultRetryOptions.timeout);

    // Resolve promise if ready event is emitted and CrossFrameworkLib is available on window
    const readyHandler = () => {
      clearTimeout(timeoutId);
      if (isCrossFrameworkLibReady()) {
        // eslint-disable-next-line no-console
        console.info('CrossFrameworkLib: Loaded via ready event (fallback method)');
        resolve((globalObj as GlobalObj & { CrossFrameworkLib: CrossFrameworkLib }).CrossFrameworkLib);
      } else {
        reject(new Error('CrossFrameworkLib not found in global after ready event'));
      }
    };

    const READY_EVENT = 'cross-framework-lib:ready';

    // If ready event is emitted, then resolve this promise
    (globalObj as GlobalObj).addEventListener(READY_EVENT, readyHandler, { once: true });

    // Function to attempt import with exponential backoff
    const attemptImport = (attempt = 0, delay = defaultRetryOptions.initialDelay) => {
      // Try to import the module directly
      importDynamically()
        .then((module) => {
          clearTimeout(timeoutId);
          // eslint-disable-next-line no-console
          console.info('CrossFrameworkLib: Dynamically imported (not found on window)');
          resolve(module.CrossFrameworkLib);
          (globalObj as GlobalObj).removeEventListener(READY_EVENT, readyHandler);
        })
        .catch((error) => {
          // Don't retry if CrossFrameworkLib is on global scope
          if (isCrossFrameworkLibReady()) {
            return;
          }
          // If we've reached max retries
          if (attempt >= defaultRetryOptions.maxRetries) {
            // eslint-disable-next-line no-console
            console.error(`CrossFrameworkLib: Import failed after ${attempt} attempts.`, error);
            return;
          }

          // Calculate next delay (with exponential backoff)
          const nextDelay = Math.min(delay * defaultRetryOptions.backoffFactor, defaultRetryOptions.maxDelay);

          // eslint-disable-next-line no-console
          console.warn(`CrossFrameworkLib: Import attempt ${attempt + 1} failed, retrying in ${delay}ms`);

          // Schedule next attempt
          setTimeout(() => attemptImport(attempt + 1, nextDelay), delay);
        });
    };

    // Start the first attempt
    try {
      attemptImport();
    } catch (error) {
      clearTimeout(timeoutId);
      reject(error);
    }
  });
}
